import React, { useState, useEffect } from 'react';
import axios from 'axios';// or your standard axios import
import { useAuth } from '../contexts/AuthContext';
import UsersTab from '../components/admin/UsersTab';
import NewsPostsTab from '../components/admin/NewsPostsTab'; 
import JobPostsTab from '../components/admin/JobPostsTab';   
import AdsTab from '../components/admin/AdsTab'; 
import AdminReportsPage from '../components/admin/AdminReportsPage';
import apiClient from '../utils/api';
import WithdrawalsTab from '../components/admin/WithdrawalsTab';

// --- NEW: IMPORT THE QUOTES TAB ---
import QuotesTab from '../components/admin/QuotesTab'; 

import './AdminDashboard.css';

const AdminDashboard = () => {
  const { firebaseUser, currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('news');
  const [allUsers, setAllUsers] = useState([]);
  const [allNews, setAllNews] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [allAds, setAllAds] = useState([]); 
  const [allWithdrawals, setAllWithdrawals] = useState([]);
  
  // --- NEW: STATE FOR QUOTES ---
  const [allQuotes, setAllQuotes] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async () => {
    if (!firebaseUser) return;
    setLoading(true);
    setError(''); // Reset error state on retry
    try {
        const idToken = await firebaseUser.getIdToken(true);
        // Use Promise.allSettled so one failing endpoint doesn't crash the page
        const results = await Promise.allSettled([
          apiClient.get('/admin/users'),
          apiClient.get('/admin/user-news'),
          apiClient.get('/admin/jobs'),
          apiClient.get('/ads', { params: { admin: true } }), 
          apiClient.get('/admin/withdrawals'),
          apiClient.get('/quotes') 
        ]);
        
        const [usersRes, newsRes, jobsRes, adsRes, withdrawRes, quotesRes] = results;
        
        // Helper to check if a request succeeded and return its data, otherwise log the error
        if (usersRes.status === 'fulfilled') {
          setAllUsers(usersRes.value.data);
        } else {
          console.error("Error fetching users:", usersRes.reason);
          // If a critical endpoint like 'users' fails, you might still want to show an error
          setError('Failed to load critical user data.');
        }

        if (newsRes.status === 'fulfilled') setAllNews(newsRes.value.data);
        else console.error("Error fetching news:", newsRes.reason);

        if (jobsRes.status === 'fulfilled') setAllJobs(jobsRes.value.data);
        else console.error("Error fetching jobs:", jobsRes.reason);

        if (adsRes.status === 'fulfilled') setAllAds(adsRes.value.data);
        else console.error("Error fetching ads:", adsRes.reason);

        if (withdrawRes.status === 'fulfilled') setAllWithdrawals(withdrawRes.value.data);
        else console.error("Error fetching withdrawals:", withdrawRes.reason);

        // Quotes can fail gracefully without breaking the rest of the dashboard
        if (quotesRes.status === 'fulfilled') {
          setAllQuotes(quotesRes.value.data);
        } else {
          console.error("Error fetching quotes (likely the 404):", quotesRes.reason);
        }

    } catch (err) { 
        console.error("Unexpected error fetching admin data:", err);
        setError('An unexpected error occurred.');
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    if (firebaseUser) {
        fetchData();
    } else {
        setLoading(false);
        setError('Please log in to access the admin dashboard.');
    }
  }, [firebaseUser]);

  if (loading) return <div className="admin-container"><p>Loading dashboard...</p></div>;
  if (error) return <div className="admin-container"><p className="error-message">{error}</p></div>;

  return (
      <div className="admin-container">
        <h1>Admin Dashboard</h1>
        
        <div className="admin-tabs">
          <button onClick={() => setActiveTab('users')} className={activeTab === 'users' ? 'active' : ''}>Users List</button>
          <button onClick={() => setActiveTab('news')} className={activeTab === 'news' ? 'active' : ''}>User News</button>
          <button onClick={() => setActiveTab('jobs')} className={activeTab === 'jobs' ? 'active' : ''}>Job Posts</button>
          <button onClick={() => setActiveTab('ads')} className={activeTab === 'ads' ? 'active' : ''}>Advertisements</button>
          <button onClick={() => setActiveTab('withdrawals')} className={activeTab === 'withdrawals' ? 'active' : ''}>Withdrawals</button> 
          
          {/* === NEW: QUOTES TAB BUTTON === */}
          <button onClick={() => setActiveTab('quotes')} className={activeTab === 'quotes' ? 'active' : ''}>Quotes</button>
          
          <button onClick={() => setActiveTab('reports')} className={activeTab === 'reports' ? 'active' : ''}>Reports</button>
        </div>
        
        {activeTab === 'users' && <UsersTab usersData={allUsers} onRefresh={fetchData} />}
        {activeTab === 'news' && <NewsPostsTab newsData={allNews} setNewsData={setAllNews} onDataRefresh={fetchData} />}
        {activeTab === 'jobs' && <JobPostsTab jobsData={allJobs} setJobsData={setAllJobs} onDataRefresh={fetchData} />}
        {activeTab === 'ads' && <AdsTab adsData={allAds} onDataRefresh={fetchData} />}
        {activeTab === 'withdrawals' && (
            <WithdrawalsTab withdrawals={allWithdrawals} onRefresh={fetchData} />
        )}
        
        {/* === NEW: RENDER THE QUOTES TAB === */}
        {activeTab === 'quotes' && <QuotesTab quotesData={allQuotes} onDataRefresh={fetchData} />}
        
        {activeTab === 'reports' && <AdminReportsPage />}
      </div>
  );
};

export default AdminDashboard;